import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  Search,
  Sparkles,
  HelpCircle,
  BookOpen,
  CheckCircle,
  CreditCard,
  UserCheck,
  ChevronDown,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  FileText,
  LifeBuoy,
  X,
  Send,
  ExternalLink,
  ArrowRight,
  ShieldAlert,
  Zap,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import '../styles/help-center.css';

// FAQ Database
const FAQ_DATA = [
  {
    id: 'ai-1',
    category: 'ai-grading',
    question: 'How does Learnly\'s AI Auto-Grading & Feedback work?',
    answer:
      'Learnly uses fine-tuned AI models to analyze essay, short-answer, and coding submission responses against course rubrics. It provides instant preliminary scores, detailed rubric breakdowns, and personalized constructive feedback to help you improve.',
    actionText: 'View Assessments',
    actionLink: '/assessments',
  },
  {
    id: 'ai-2',
    category: 'ai-grading',
    question: 'Can an instructor override AI-generated assessment grades?',
    answer:
      'Yes! Instructors and admins have full manual grading controls. They can review AI grade suggestions, modify question-by-question marks, add custom instructor comments, and regrade submissions at any time.',
    actionText: 'Manage Assessments',
    actionLink: '/admin/assessments',
  },
  {
    id: 'gs-1',
    category: 'getting-started',
    question: 'How do I complete my student account onboarding and select a campus?',
    answer:
      'After registering your email and verifying via OTP or Cognito, navigate to the Campus Selector page. Select your campus (e.g., Jalusi Tech - Main Campus) to view your enrolled cohorts and courses.',
    actionText: 'Go to Campus Switcher',
    actionLink: '/profile/switch-campus',
  },
  {
    id: 'gs-2',
    category: 'getting-started',
    question: 'Where can I see my current active course enrollments?',
    answer:
      'Go to "My Enrollments" in the main sidebar. You will see active courses, progress tracking bars, current grades, and cohort assignment status.',
    actionText: 'My Enrollments',
    actionLink: '/enrollments',
  },
  {
    id: 'as-1',
    category: 'assessments',
    question: 'What happens if I lose internet connection while taking a quiz?',
    answer:
      'Learnly auto-saves your answer progress locally every 15 seconds. If your connection drops, stay on the page. Once reconnected, click "Sync Answers" or submit your assessment. Timer state is synced with the server.',
    actionText: 'Go to Assessments',
    actionLink: '/assessments',
  },
  {
    id: 'as-2',
    category: 'assessments',
    question: 'How can I view detailed feedback on my past submissions?',
    answer:
      'Navigate to Assessments -> Submissions (or click "My Analytics"). Select any completed assessment to inspect your question-by-question breakdown, AI rubric feedback, and correct answers.',
    actionText: 'View Submissions',
    actionLink: '/assessments/submissions',
  },
  {
    id: 'ep-1',
    category: 'enrollments',
    question: 'How long does payment verification (Proof of Payment) take?',
    answer:
      'Once you upload your POP receipt, our finance team reviews and verifies it within 24 business hours. You can monitor your verification status under "My Enrollments" or contact campus finance directly.',
    actionText: 'Check Payment Status',
    actionLink: '/enrollments',
  },
  {
    id: 'ep-2',
    category: 'enrollments',
    question: 'How do admins batch enroll students into a cohort?',
    answer:
      'Campus admins can navigate to Admin -> Manage Cohorts or Admin -> Manage Students. Select target students or upload a CSV file to enroll the entire cohort in one step.',
    actionText: 'Manage Cohorts',
    actionLink: '/admin/cohorts',
  },
  {
    id: 'acc-1',
    category: 'account',
    question: 'How do I update my profile details or change my avatar?',
    answer:
      'Navigate to System Settings in the bottom sidebar. There you can update your first/last name, phone number, notification preferences, and manage security settings.',
    actionText: 'System Settings',
    actionLink: '/profile',
  },
  {
    id: 'acc-2',
    category: 'account',
    question: 'How does Multi-Factor & OTP Login work?',
    answer:
      'Learnly supports secure OTP sign-in. When logging in, enter your registered email or phone number. A 6-digit one-time code will be dispatched to authenticate your session safely.',
    actionText: 'Account Profile',
    actionLink: '/profile',
  },
];

// Pre-configured AI Prompts
const AI_PROMPTS = [
  {
    chip: '⚡ How does AI auto-grading work?',
    query: 'How does AI auto-grading work in Learnly?',
    answer:
      'Learnly\'s AI model evaluates your open-ended essay and short code responses against rubrics provided by instructors. It computes a preliminary grade, checks key criteria, and provides instant line-by-line feedback. Instructors review and finalize all grades.',
    actionText: 'View Assessments',
    actionLink: '/assessments',
  },
  {
    chip: '📝 How do I submit an assessment?',
    query: 'How do I submit an assessment?',
    answer:
      'Go to "Assessments" from the sidebar, select your active quiz or assignment, complete all required questions, and click "Submit Assessment". Answers auto-save continuously while working.',
    actionText: 'Open Assessments',
    actionLink: '/assessments',
  },
  {
    chip: '💳 How to check POP payment status?',
    query: 'How to check POP payment status?',
    answer:
      'Navigate to "My Enrollments". Look for the status badge next to your course enrollment. Statuses include: Pending Verification, Approved, or Action Required. Finance usually processes receipts within 24 hours.',
    actionText: 'Check Enrollments',
    actionLink: '/enrollments',
  },
  {
    chip: '🏫 How to switch campuses?',
    query: 'How to switch campuses?',
    answer:
      'Go to System Settings -> Switch Campus (or click your profile menu). Select your target campus from the dropdown list to switch your active learning context.',
    actionText: 'Switch Campus',
    actionLink: '/profile/switch-campus',
  },
];

const CATEGORIES = [
  { id: 'all', label: 'All Topics', icon: BookOpen },
  { id: 'ai-grading', label: 'AI & Auto-Grading', icon: Sparkles },
  { id: 'getting-started', label: 'Getting Started', icon: Zap },
  { id: 'assessments', label: 'Quizzes & Assessments', icon: CheckCircle },
  { id: 'enrollments', label: 'Enrollment & Payments', icon: CreditCard },
  { id: 'account', label: 'Account & Settings', icon: UserCheck },
];

export default function HelpCenter() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedFaqId, setExpandedFaqId] = useState(FAQ_DATA[0].id);
  const [votes, setVotes] = useState({});
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);

  // AI Assistant State
  const [activeAiAnswer, setActiveAiAnswer] = useState(null);
  const [customAiQuery, setCustomAiQuery] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);

  // Support Ticket Form state
  const [ticketForm, setTicketForm] = useState({
    subject: '',
    category: 'general',
    message: '',
    priority: 'normal',
  });

  // Filter FAQs based on category and search query
  const filteredFaqs = useMemo(() => {
    return FAQ_DATA.filter((faq) => {
      const matchesCategory =
        selectedCategory === 'all' || faq.category === selectedCategory;
      const matchesSearch =
        !searchQuery.trim() ||
        faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCategory && matchesSearch;
    });
  }, [selectedCategory, searchQuery]);

  const handleVote = (faqId, type) => {
    setVotes((prev) => ({
      ...prev,
      [faqId]: prev[faqId] === type ? null : type,
    }));
    toast.success(
      type === 'yes'
        ? 'Thanks for your feedback!'
        : 'Thank you. We will work to improve this answer.'
    );
  };

  const handlePromptClick = (prompt) => {
    setIsAiLoading(true);
    setActiveAiAnswer(null);
    setTimeout(() => {
      setActiveAiAnswer({
        query: prompt.query,
        answer: prompt.answer,
        actionText: prompt.actionText,
        actionLink: prompt.actionLink,
      });
      setIsAiLoading(false);
    }, 300);
  };

  const handleCustomAiSearch = (e) => {
    e.preventDefault();
    if (!customAiQuery.trim()) return;

    setIsAiLoading(true);
    setActiveAiAnswer(null);

    setTimeout(() => {
      // Fuzzy search in FAQ dataset for custom query
      const match = FAQ_DATA.find(
        (faq) =>
          faq.question.toLowerCase().includes(customAiQuery.toLowerCase()) ||
          faq.answer.toLowerCase().includes(customAiQuery.toLowerCase())
      );

      if (match) {
        setActiveAiAnswer({
          query: customAiQuery,
          answer: match.answer,
          actionText: match.actionText,
          actionLink: match.actionLink,
        });
      } else {
        setActiveAiAnswer({
          query: customAiQuery,
          answer:
            'I analyzed your query: "' +
            customAiQuery +
            '". For specific questions regarding your student status, course grading, or payment receipts, please open a support ticket below or contact your campus administrator.',
          actionText: 'Submit Support Ticket',
          actionLink: '#support-modal',
          isSupportTrigger: true,
        });
      }
      setIsAiLoading(false);
    }, 400);
  };

  const handleTicketSubmit = (e) => {
    e.preventDefault();
    if (!ticketForm.subject.trim() || !ticketForm.message.trim()) {
      toast.error('Please complete all required fields.');
      return;
    }
    toast.success('Support ticket submitted successfully! Ticket ID: #LNK-' + Math.floor(1000 + Math.random() * 9000));
    setIsSupportModalOpen(false);
    setTicketForm({ subject: '', category: 'general', message: '', priority: 'normal' });
  };

  return (
    <DashboardLayout>
      <div className="help-center-container">
        {/* Header Card */}
        <div className="help-header-card">
          <div className="help-header-top">
            <div className="help-title-group">
              <span className="help-badge">
                <LifeBuoy size={14} /> Help Center & AI Knowledge Base
              </span>
              <h1 className="help-title">How can we help you today?</h1>
              <p className="help-subtitle">
                Search our interactive AI knowledge base, guides, or ask the Learnly AI Assistant.
              </p>
            </div>
            <button
              onClick={() => setIsSupportModalOpen(true)}
              className="btn btn-primary flex items-center gap-sm"
              type="button"
            >
              <MessageSquare size={18} /> Contact Support Desk
            </button>
          </div>

          {/* Instant Search Bar */}
          <div className="help-search-box">
            <Search className="help-search-icon" size={20} />
            <input
              type="text"
              className="help-search-input"
              placeholder="Search help topics (e.g. AI grading, quiz submission, POP verification)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                className="help-search-clear"
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                type="button"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* AI Assistant Sandbox Widget */}
        <div className="ai-help-card">
          <div className="ai-card-header">
            <span className="ai-tag">
              <Sparkles size={14} /> Ask Learnly AI Assistant
            </span>
            <span className="text-xs text-muted">Powered by Learnly AI Engine</span>
          </div>

          <form onSubmit={handleCustomAiSearch} className="flex gap-sm">
            <input
              type="text"
              className="form-input flex-1"
              placeholder="Ask any question to the Learnly AI assistant..."
              value={customAiQuery}
              onChange={(e) => setCustomAiQuery(e.target.value)}
            />
            <button
              type="submit"
              className="btn btn-secondary flex items-center gap-xs"
              disabled={isAiLoading || !customAiQuery.trim()}
            >
              <Send size={16} /> Ask AI
            </button>
          </form>

          <div>
            <div className="ai-prompts-label">Popular AI Quick Prompts</div>
            <div className="ai-prompt-chips">
              {AI_PROMPTS.map((prompt, index) => (
                <button
                  key={index}
                  onClick={() => handlePromptClick(prompt)}
                  className="ai-prompt-chip"
                  type="button"
                >
                  {prompt.chip}
                </button>
              ))}
            </div>
          </div>

          {isAiLoading && (
            <div className="ai-response-box">
              <div className="ai-response-header">
                <span><Sparkles size={16} /> Learnly AI is analyzing your question...</span>
              </div>
            </div>
          )}

          {activeAiAnswer && !isAiLoading && (
            <div className="ai-response-box">
              <div className="ai-response-header">
                <span><Sparkles size={16} /> AI Answer for: "{activeAiAnswer.query}"</span>
                <button
                  onClick={() => setActiveAiAnswer(null)}
                  className="bg-transparent border-none cursor-pointer text-muted p-0"
                  type="button"
                >
                  <X size={16} />
                </button>
              </div>
              <p className="ai-response-content">{activeAiAnswer.answer}</p>
              {activeAiAnswer.isSupportTrigger ? (
                <button
                  onClick={() => setIsSupportModalOpen(true)}
                  className="ai-response-action cursor-pointer bg-transparent border-none"
                  type="button"
                >
                  {activeAiAnswer.actionText} <ArrowRight size={14} />
                </button>
              ) : (
                <Link to={activeAiAnswer.actionLink} className="ai-response-action">
                  {activeAiAnswer.actionText} <ExternalLink size={14} />
                </Link>
              )}
            </div>
          )}
        </div>

        {/* Category Navigation Tabs */}
        <div className="category-nav">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            const isActive = selectedCategory === cat.id;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`category-tab ${isActive ? 'active' : ''}`}
                type="button"
              >
                <Icon size={16} />
                <span>{cat.label}</span>
              </button>
            );
          })}
        </div>

        {/* FAQ Accordion List */}
        <div className="faq-section">
          {filteredFaqs.length === 0 ? (
            <div className="help-header-card text-center p-xl">
              <ShieldAlert className="mx-auto text-muted mb-sm" size={32} />
              <h3 className="font-bold text-lg mb-xs">No matching topics found</h3>
              <p className="text-muted text-sm mb-md">
                Try searching with different keywords or ask our AI Assistant above.
              </p>
              <button
                onClick={() => {
                  setSearchQuery('');
                  setSelectedCategory('all');
                }}
                className="btn btn-secondary inline-flex items-center gap-xs mx-auto"
                type="button"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            filteredFaqs.map((faq) => {
              const isOpen = expandedFaqId === faq.id;
              const userVote = votes[faq.id];

              return (
                <div key={faq.id} className={`faq-card ${isOpen ? 'open' : ''}`}>
                  <button
                    className="faq-header"
                    onClick={() => setExpandedFaqId(isOpen ? null : faq.id)}
                    type="button"
                  >
                    <div className="faq-header-title">
                      <span className="faq-category-pill">
                        {CATEGORIES.find((c) => c.id === faq.category)?.label || faq.category}
                      </span>
                      <span>{faq.question}</span>
                    </div>
                    <ChevronDown className="faq-toggle-icon" size={20} />
                  </button>

                  {isOpen && (
                    <div className="faq-body">
                      <p>{faq.answer}</p>
                      {faq.actionLink && (
                        <div>
                          <Link
                            to={faq.actionLink}
                            className="inline-flex items-center gap-xs text-primary font-bold text-xs"
                          >
                            {faq.actionText} <ExternalLink size={14} />
                          </Link>
                        </div>
                      )}
                      <div className="faq-actions">
                        <span>Was this topic helpful?</span>
                        <div className="faq-vote-btns">
                          <button
                            onClick={() => handleVote(faq.id, 'yes')}
                            className={`faq-vote-btn ${userVote === 'yes' ? 'voted' : ''}`}
                            type="button"
                          >
                            <ThumbsUp size={14} /> Yes
                          </button>
                          <button
                            onClick={() => handleVote(faq.id, 'no')}
                            className={`faq-vote-btn ${userVote === 'no' ? 'voted' : ''}`}
                            type="button"
                          >
                            <ThumbsDown size={14} /> No
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Quick Resource Cards Grid */}
        <div className="quick-cards-grid">
          <div className="support-card">
            <div className="support-card-header">
              <div className="support-icon-wrapper">
                <FileText size={22} />
              </div>
              <div>
                <h3 className="support-card-title">Documentation & Guides</h3>
                <p className="support-card-desc">
                  Browse detailed course, assessment, and instructor manuals.
                </p>
              </div>
            </div>
            <Link to="/courses" className="btn btn-secondary text-sm flex items-center justify-between">
              Browse Platform Courses <ArrowRight size={16} />
            </Link>
          </div>

          <div className="support-card">
            <div className="support-card-header">
              <div className="support-icon-wrapper">
                <Sparkles size={22} />
              </div>
              <div>
                <h3 className="support-card-title">AI Assistant Best Practices</h3>
                <p className="support-card-desc">
                  Learn how to leverage AI feedback to boost assessment scores.
                </p>
              </div>
            </div>
            <Link to="/assessments" className="btn btn-secondary text-sm flex items-center justify-between">
              Take Assessments <ArrowRight size={16} />
            </Link>
          </div>

          <div className="support-card">
            <div className="support-card-header">
              <div className="support-icon-wrapper">
                <MessageSquare size={22} />
              </div>
              <div>
                <h3 className="support-card-title">Need Direct Support?</h3>
                <p className="support-card-desc">
                  Our campus support team and finance desk are available 24/7.
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsSupportModalOpen(true)}
              className="btn btn-primary text-sm flex items-center justify-between"
              type="button"
            >
              Submit Support Ticket <ArrowRight size={16} />
            </button>
          </div>
        </div>

        {/* Interactive Support Modal */}
        {isSupportModalOpen && (
          <div className="modal-overlay" onClick={() => setIsSupportModalOpen(false)}>
            <div className="modal-card" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3 className="modal-title">Submit a Support Ticket</h3>
                <button
                  onClick={() => setIsSupportModalOpen(false)}
                  className="modal-close-btn"
                  aria-label="Close modal"
                  type="button"
                >
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleTicketSubmit} className="flex flex-col gap-md">
                <div className="form-group">
                  <label className="form-label">Issue Category</label>
                  <select
                    className="form-select"
                    value={ticketForm.category}
                    onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}
                  >
                    <option value="general">General Support</option>
                    <option value="ai-grading">AI Grading & Feedback</option>
                    <option value="assessment">Quiz or Assessment Bug</option>
                    <option value="payment">Payment Verification & Receipts</option>
                    <option value="account">Account & Campus Access</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Subject</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Brief description of the issue..."
                    value={ticketForm.subject}
                    onChange={(e) => setTicketForm({ ...ticketForm, subject: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Urgency Level</label>
                  <select
                    className="form-select"
                    value={ticketForm.priority}
                    onChange={(e) => setTicketForm({ ...ticketForm, priority: e.target.value })}
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High (Urgent Assessment/Payment)</option>
                    <option value="critical">Critical (Unable to log in)</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Detailed Explanation</label>
                  <textarea
                    className="form-textarea"
                    placeholder="Provide relevant details, course names, or error descriptions..."
                    value={ticketForm.message}
                    onChange={(e) => setTicketForm({ ...ticketForm, message: e.target.value })}
                    required
                  />
                </div>

                <div className="flex justify-end gap-sm pt-sm">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setIsSupportModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary flex items-center gap-xs">
                    <Send size={16} /> Submit Ticket
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
